import { createInjectionState } from '@vueuse/core';
import { ref, unref } from 'vue';

import type { ModalDialogApi } from './bootstrap-modal';

export type DefaultConfirmationArgs = {
    message?: string;
    cancel?: boolean;
} | undefined;

export type ConfirmationDialogApi<T extends [DefaultConfirmationArgs] = [DefaultConfirmationArgs], R = void>
= ModalDialogApi<T, [], R>;
export type ConfirmationFunction<T extends DefaultConfirmationArgs = DefaultConfirmationArgs, R = void>
= (message?: string, args?: T) => Promise<R>;

const [useProvideGlobalConfirm, useGlobalConfirmOptional] = createInjectionState((
    confirm: ConfirmationFunction,
) => ({
    confirm,
}));

export { useProvideGlobalConfirm };

export function useConfirm<T extends DefaultConfirmationArgs = DefaultConfirmationArgs, R = void>(provide?: boolean) {
    const confirmApi = ref<ConfirmationDialogApi<[T], R> | undefined>();
    const confirm: ConfirmationFunction<T, R> = function(message?: string, args?: T) {
        const _api = unref(confirmApi);
        if(!_api) {
            return Promise.reject(new Error('no api'));
        }
        // eslint-disable-next-line no-param-reassign
        if(!message) message = 'Confermi?';
        return _api.showModal(undefined, { message, ...args } as T);
    };
    if(provide) {
        useProvideGlobalConfirm(confirm as ConfirmationFunction);
    }
    return [
        confirmApi,
        confirm,
    ] as const;
}

export function useGlobalConfirm() {
    const handler = useGlobalConfirmOptional();
    if(!handler) {
        throw new Error('Confirmation is not provided');
    }
    return handler.confirm;
}
